import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { Page, PageHeader, Empty } from "@/components/shared";
import { fmtDate } from "@/lib/labels";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { FileText, Download, Eye, Upload, FolderArchive, Folder, Lock, ChevronRight, ArrowLeft, Search } from "lucide-react";

const CATEGORY_LABELS = {
  kyc: "KYC",
  legitimasjon: "Legitimasjon",
  eierstruktur: "Eierstruktur",
  fullmakter: "Fullmakter",
  oppdragsavtaler: "Oppdragsavtaler",
  signerte_dokumenter: "Signerte dokumenter",
  arbeidsavtaler: "Arbeidsavtaler",
  signeringssertifikater: "Signeringssertifikater",
  tilbud: "Tilbud",
  korrespondanse: "Korrespondanse",
  selskapsdokumentasjon: "Selskapsdokumentasjon",
  oppsigelse: "Oppsigelse",
  ovrige: "Øvrige",
};
// Rekkefølge på mappene
const CATEGORY_ORDER = Object.keys(CATEGORY_LABELS);
const catLabel = (k) => CATEGORY_LABELS[k] || k;

function UploadDialog({ defaultCat, customers, onDone }) {
  const [open, setOpen] = useState(false);
  const [cat, setCat] = useState(defaultCat || "ovrige");
  const [customerId, setCustomerId] = useState("none");
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) { setCat(defaultCat || "ovrige"); setCustomerId("none"); setFile(null); } }, [open, defaultCat]);

  const submit = async () => {
    if (!file) { toast.error("Velg en fil"); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("category", cat);
      if (customerId !== "none") fd.append("customer_id", customerId);
      await api.post("/documents/upload", fd);
      toast.success("Dokument lastet opp");
      setOpen(false);
      onDone?.(cat);
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button data-testid="doc-upload-open" className="bg-white text-black hover:bg-white/90"><Upload className="w-4 h-4 mr-1" /> Last opp</Button>
      </DialogTrigger>
      <DialogContent className="bg-popover border-border">
        <DialogHeader><DialogTitle>Last opp dokument</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div>
            <Label className="label-caps">Mappe / kategori</Label>
            <Select value={cat} onValueChange={setCat}>
              <SelectTrigger className="mt-1 bg-secondary border-border" data-testid="doc-upload-category"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-popover border-border">
                {CATEGORY_ORDER.map((k) => <SelectItem key={k} value={k}>{catLabel(k)}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="label-caps">Kunde (valgfritt)</Label>
            <Select value={customerId} onValueChange={setCustomerId}>
              <SelectTrigger className="mt-1 bg-secondary border-border" data-testid="doc-upload-customer"><SelectValue placeholder="Ingen (generelt dokument)" /></SelectTrigger>
              <SelectContent className="bg-popover border-border max-h-64">
                <SelectItem value="none">Ingen (generelt dokument)</SelectItem>
                {customers.map((c) => <SelectItem key={c.id} value={c.id}>{c.legal_name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="label-caps">Fil</Label>
            <Input type="file" onChange={(e) => setFile(e.target.files[0])} className="mt-1 bg-secondary border-border" data-testid="doc-upload-file" />
          </div>
        </div>
        <DialogFooter>
          <Button onClick={submit} disabled={busy} data-testid="doc-upload-submit" className="bg-white text-black hover:bg-white/90">
            {busy ? "Laster opp…" : "Last opp"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function Documents() {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [openCat, setOpenCat] = useState(null);
  const [customers, setCustomers] = useState([]);
  const nav = useNavigate();

  const load = () => api.get("/all-documents").then((r) => setRows(r.data));
  useEffect(() => { load(); api.get("/customers").then((r) => setCustomers(r.data)).catch(() => {}); }, []);

  const download = async (d) => {
    const res = await api.get(`/documents/${d.id}/download`, { responseType: "blob" });
    const url = URL.createObjectURL(res.data); const a = document.createElement("a"); a.href = url; a.download = d.name; a.click();
  };
  const view = async (d) => {
    try {
      const res = await api.get(`/documents/${d.id}/view`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      window.open(url, "_blank", "noopener,noreferrer");
    } catch (e) { toast.error(apiError(e)); }
  };

  // Grupper dokumenter per kategori
  const folders = useMemo(() => {
    const map = {};
    for (const d of rows) {
      const c = d.category || "ovrige";
      (map[c] = map[c] || []).push(d);
    }
    const keys = [...new Set([...CATEGORY_ORDER, ...Object.keys(map)])];
    return keys
      .filter((k) => CATEGORY_ORDER.includes(k) || (map[k] && map[k].length))
      .map((k) => ({ key: k, label: catLabel(k), docs: map[k] || [] }));
  }, [rows]);

  const matches = (d) =>
    d.name.toLowerCase().includes(q.toLowerCase()) ||
    (d.customer_name || "").toLowerCase().includes(q.toLowerCase());

  // Søk på tvers av mapper: vis flat liste når det søkes
  const searching = q.trim().length > 0;
  const searchResults = searching ? rows.filter(matches) : [];

  const current = openCat ? folders.find((f) => f.key === openCat) : null;
  const currentDocs = current ? current.docs : [];

  const DocTable = ({ docs, showCategory }) => (
    <div className="bg-card border border-border rounded-xl overflow-hidden">
      <table className="w-full text-sm">
        <thead><tr className="border-b border-border text-left">
          {["Dokument", ...(showCategory ? ["Mappe"] : []), "Kunde", "Lastet opp av", "Dato", ""].map((h) => <th key={h} className="px-4 py-3 label-caps">{h}</th>)}
        </tr></thead>
        <tbody>
          {docs.map((d) => (
            <tr key={d.id} className="border-b border-border/50 hover:bg-accent/30" data-testid={`doc-row-${d.id}`}>
              <td className="px-4 py-3 font-medium flex items-center gap-2"><FileText className="w-4 h-4 text-muted-foreground" /> {d.name} {d.confidential && <Lock className="w-3 h-3 text-amber-400" />}</td>
              {showCategory && <td className="px-4 py-3 text-muted-foreground">{catLabel(d.category)}</td>}
              <td className="px-4 py-3"><button className="hover:underline" onClick={() => nav(`/kunder/${d.customer_id}`)}>{d.customer_name}</button></td>
              <td className="px-4 py-3 text-muted-foreground">{d.uploaded_by}</td>
              <td className="px-4 py-3 text-muted-foreground text-xs">{fmtDate(d.uploaded_at)}</td>
              <td className="px-4 py-3">
                <div className="flex items-center gap-1 justify-end">
                  <button onClick={() => view(d)} data-testid={`doc-view-${d.id}`} title="Vis" className="text-muted-foreground hover:text-foreground p-1"><Eye className="w-4 h-4" /></button>
                  <button onClick={() => download(d)} data-testid={`doc-download-${d.id}`} title="Last ned" className="text-muted-foreground hover:text-foreground p-1"><Download className="w-4 h-4" /></button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  return (
    <Page>
      <PageHeader title="Dokumenter" subtitle="Arkiv organisert i mapper per kategori" testid="documents-header">
        <UploadDialog defaultCat={openCat} customers={customers} onDone={(cat) => { load(); setOpenCat(cat); }} />
      </PageHeader>

      <div className="relative mb-6 max-w-sm">
        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
        <input data-testid="doc-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Søk dokument eller kunde…"
          className="w-full h-10 pl-9 pr-3 rounded-md bg-secondary border border-border text-sm" />
      </div>

      {searching ? (
        searchResults.length === 0 ? <Empty icon={Search} title="Ingen treff" /> : <DocTable docs={searchResults} showCategory />
      ) : openCat ? (
        <div>
          <div className="flex items-center gap-2 text-sm mb-4" data-testid="doc-breadcrumb">
            <button onClick={() => setOpenCat(null)} className="flex items-center gap-1 text-muted-foreground hover:text-foreground" data-testid="doc-back">
              <ArrowLeft className="w-4 h-4" /> Dokumenter
            </button>
            <ChevronRight className="w-4 h-4 text-muted-foreground" />
            <span className="font-medium">{current?.label}</span>
            <span className="text-muted-foreground">· {currentDocs.length} dokument{currentDocs.length === 1 ? "" : "er"}</span>
          </div>
          {currentDocs.length === 0 ? <Empty icon={FolderArchive} title="Tom mappe" hint="Ingen dokumenter i denne kategorien ennå." /> : <DocTable docs={currentDocs} />}
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="doc-folders">
          {folders.map((f) => (
            <button key={f.key} onClick={() => setOpenCat(f.key)} data-testid={`doc-folder-${f.key}`}
              className="group text-left bg-card border border-border rounded-xl p-5 hover:border-foreground/30 hover:bg-accent/20 transition-colors">
              <div className="flex items-center justify-between">
                <div className="w-11 h-11 rounded-lg bg-secondary flex items-center justify-center group-hover:bg-secondary/70">
                  <Folder className="w-5 h-5 text-foreground/80" />
                </div>
                <ChevronRight className="w-4 h-4 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
              </div>
              <p className="font-medium mt-4">{f.label}</p>
              <p className="text-xs text-muted-foreground mt-0.5">{f.docs.length} dokument{f.docs.length === 1 ? "" : "er"}</p>
            </button>
          ))}
        </div>
      )}
    </Page>
  );
}
